import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import { cn } from '@Utils/cn';
import { GuestScorecards } from '@Components/Guest';
import type { GuestProfile, GuestStatusTone } from '@Types/shiftReport';

const TONE: Record<GuestStatusTone, string> = {
  vip: 'text-amber-600',
  positive: 'text-green-600',
  neutral: 'text-zinc-500',
  alert: 'text-red-600',
};

function DetailRow({ icon, children }: { icon: string; children: ReactNode }) {
  return (
    <div className="flex gap-2 border-b border-zinc-100 px-3 py-2 text-sm last:border-b-0">
      <span className="w-5 shrink-0 text-center">{icon}</span>
      <span className="text-zinc-700">{children}</span>
    </div>
  );
}

export default function GuestProfileCard({ guest }: { guest: GuestProfile }) {
  const { history } = guest;
  const visits =
    history.priorVisits > 0
      ? `${history.priorVisits} prior${history.lastVisitDate ? ` · last on ${history.lastVisitDate}` : ''}`
      : 'First visit';

  return (
    <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm">
      {/* Header */}
      <div className="border-b border-zinc-200 bg-zinc-50 p-4">
        <div className="flex items-center gap-3">
          {guest.avatarUrl && (
            <img
              src={guest.avatarUrl}
              alt={guest.name}
              className="h-14 w-14 rounded-full bg-zinc-200 object-cover ring-2 ring-white"
            />
          )}
          <div className="min-w-0">
            {guest.statusLabel && (
              <div
                className={cn(
                  'text-[11px] font-bold tracking-wide uppercase',
                  TONE[guest.statusTone ?? 'neutral'],
                )}
              >
                {guest.statusLabel}
              </div>
            )}
            {guest.fishId ? (
              <Link
                to={`/guests/${guest.fishId}`}
                className="text-lg font-bold text-zinc-900 hover:underline"
              >
                {guest.name}
              </Link>
            ) : (
              <div className="text-lg font-bold text-zinc-900">
                {guest.name}
              </div>
            )}
            <div className="text-xs text-zinc-500">
              {guest.maskedPhone}
              {guest.email && <> · {guest.email}</>}
            </div>
          </div>
        </div>

        {guest.tags.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {guest.tags.map((tag) => (
              <span
                key={tag}
                className="rounded-full bg-[#d3a664] px-3 py-1 text-xs font-semibold text-white"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        {guest.guestNotes && (
          <div className="mb-4 rounded-md border-l-4 border-blue-500 bg-blue-50 px-3 py-2">
            <div className="text-[11px] font-bold tracking-wide text-blue-600 uppercase">
              Guest Notes
            </div>
            <p className="mt-0.5 text-sm text-blue-900">{guest.guestNotes}</p>
          </div>
        )}

        {guest.guestRequest && (
          <div className="mb-4 rounded-md border-l-4 border-amber-500 bg-amber-50 px-3 py-2">
            <div className="text-[11px] font-bold tracking-wide text-amber-600 uppercase">
              Guest Request
            </div>
            <p className="mt-0.5 text-sm text-amber-900">
              {guest.guestRequest}
            </p>
          </div>
        )}

        {guest.enrichment && (
          <div className="mb-4 rounded-md border-l-4 border-purple-500 bg-purple-50/60 px-3 py-2 dark:bg-purple-950/20">
            <div className="text-[11px] font-bold tracking-wide text-purple-600 uppercase dark:text-purple-300">
              Profile+
            </div>
            {guest.enrichment.identityLine && (
              <p className="mt-0.5 text-sm text-purple-900 dark:text-purple-100">
                {guest.enrichment.identityLine}
              </p>
            )}
            {guest.enrichment.jobTitle && (
              <p className="text-sm text-purple-900 dark:text-purple-100">
                {guest.enrichment.jobTitle}
              </p>
            )}
          </div>
        )}

        <GuestScorecards scorecards={guest.scorecards} className="mb-4" />

        <div className="mb-4 rounded-md bg-zinc-900 px-3 py-2 text-sm text-white">
          Time: <strong>{guest.reservation.time}</strong> · Covers:{' '}
          <strong>{guest.reservation.covers}</strong>
          {guest.reservation.table && (
            <>
              {' '}
              · Table: <strong>{guest.reservation.table}</strong>
            </>
          )}
        </div>

        <div className="rounded-lg border border-zinc-200">
          {guest.topItems.length > 0 && (
            <DetailRow icon="🍽️">
              <strong>Usual Orders:</strong>{' '}
              {guest.topItems.map((i) => `${i.name} (${i.count})`).join(', ')}
            </DetailRow>
          )}
          {guest.aiSummary && (
            <DetailRow icon="✨">
              <strong>Summary:</strong> {guest.aiSummary}
            </DetailRow>
          )}
          {guest.aiSuggestion && (
            <DetailRow icon="🪄">
              <strong>Suggestion:</strong> {guest.aiSuggestion}
            </DetailRow>
          )}
          <DetailRow icon="🏛️">
            <strong>Visits Here:</strong> {visits}
            {history.noShows > 0 && (
              <span className="text-red-600">
                {' '}
                · {history.noShows} no-show
                {history.noShows === 1 ? '' : 's'}
              </span>
            )}
          </DetailRow>
          <DetailRow icon="📍">
            <strong>Places Visited:</strong>{' '}
            {guest.placesVisited ?? 'First-time guest'}
          </DetailRow>
          <DetailRow icon="👤">
            <strong>History Metrics:</strong> MTD{' '}
            <span className="font-medium text-green-700">
              {history.mtdSpend}
            </span>{' '}
            · YTD{' '}
            <span className="font-medium text-green-700">
              {history.ytdSpend}
            </span>{' '}
            · All-Time{' '}
            <span className="font-medium text-green-700">
              {history.allTimeSpend}
            </span>
          </DetailRow>
        </div>
      </div>
    </div>
  );
}
